import type {
  AllPagesQuery,
  Image,
  LocaleString,
  ProjectsQuery,
  ProjectsQueryVariables,
} from '@/.generated/sanityTypes/graphql'
import type { GetStaticPropsContext, GetStaticPropsResult } from 'next/types'

import React from 'react'
import { useRouter } from 'next/router'

import Layout from '@/components/layout'
import ProjectsList from '@/components/projectsList'
import PageSection from '@/components/pageSection'
import Card from '@/components/card'
import { request } from '@/lib/sanityClient'
import { projects as projectsQuery } from './projects.sanity.graphql'
import { isLocalString } from '../lib/typeHelper'
import { getAllPages, getPageQueryVariables } from '@/lib/query'

type ProjectsProps = {
  data: ProjectsQuery
  messages: unknown
  isPreview: boolean
  allPages: AllPagesQuery
}

const Projects = ({ data, isPreview, allPages }: ProjectsProps) => {
  const { locale = 'de' } = useRouter()
  const page = data.allPage[0]

  return (
    <Layout
      allPages={allPages}
      isPreview={isPreview}
      title={
        (isLocalString(page?.pageHeader?.sectionTitle) &&
          page?.pageHeader?.sectionTitle[locale as keyof LocaleString]) ||
        undefined
      }
      headerFiles={
        [
          ...(page?.image ? [page?.image as Image] : []),
          ...(page?.additionalImages || []),
        ] as Image[]
      }
    >
      <Card headerData={page?.pageHeader} contentData={page} />

      {page?.aboutUsSections?.map((section: any) => {
        return <PageSection key={section.id} data={section} />
      })}

      <ProjectsList data={data} />
    </Layout>
  )
}

export async function getStaticProps(
  context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<ProjectsProps>> {
  const isPreview = context.preview || false
  const queryVariables = getPageQueryVariables('projects')

  const pageData = await request<ProjectsQuery, ProjectsQueryVariables>(
    projectsQuery,
    queryVariables,
    isPreview,
  )

  if (pageData.errors)
    throw new Error(`graphql Error occured: ${pageData.errors[0].message}`)

  return {
    props: {
      data: pageData.data,
      messages: (await import(`@/translation/${context.locale}.json`)).default,
      isPreview,
      allPages: await getAllPages(),
    },
  }
}

export default Projects
